import React, { useState, useEffect, useMemo } from 'react';
import { FaFileInvoice, FaCheck, FaFileExport } from 'react-icons/fa';
import { Button, Form, InputGroup, Placeholder, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getConfig, serverUrl } from '../../../../serverUrl';
import PaymentRow from './_payment_row';

const PaymentHistory = ({
  approvalData,
  isLoading,
  refreshApprovals
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [apartmentFilter, setApartmentFilter] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const itemsPerPage = 10;
  const ConfigData = getConfig();

  // Get unique apartment names for filter dropdown
  const apartmentOptions = approvalData
    ? Array.from(
      new Set(
        approvalData
          .map(t => t.apartment)
          .filter(Boolean)
      )
    )
    : [];

  // Filter tenants based on search term and apartment filter
  const filteredTenants = useMemo(() => {
    return approvalData
      ? approvalData
        .filter(tenant => {
          const searchLower = searchTerm.toLowerCase();
          const matchesSearch =
            (tenant.name && tenant.name.toLowerCase()?.includes(searchLower)) ||
            (tenant.phone_number && tenant.phone_number.includes(searchTerm)) ||
            (tenant.house_number && tenant.house_number.toLowerCase()?.includes(searchLower)) ||
            (tenant.apartment && tenant.apartment.toLowerCase()?.includes(searchLower)) ||
            (tenant.kra_pin && tenant.kra_pin.toString().includes(searchTerm));

          const matchesApartment = !apartmentFilter || tenant.apartment === apartmentFilter;
          return matchesSearch && matchesApartment;
        })
        .sort((a, b) => {
          return (a.approved === b.approved) ? 0 : a.approved ? 1 : -1;
        })
      : [];
  }, [approvalData, searchTerm, apartmentFilter]);

  // Calculate pagination with filtered tenants
  const totalPages = Math.ceil(filteredTenants?.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTenants.slice(indexOfFirstItem, indexOfLastItem);

  // Reset select all when filters change
  useEffect(() => {
    setSelectAll(false);
    setSelectedTenants([]);
  }, [searchTerm, apartmentFilter]);

  // Change page
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredTenants.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'User', 'Phone Number', 'House Number', 'House ID',
      'Apartment', 'Apartment ID', 'Month', 'Year',
      'Expected Amount', 'Opening Balance', 'Rent Amount', 'Status'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredTenants.map(tenant => [
        `"${tenant.name || ''}"`,
        `"${tenant.phone_number || ''}"`,
        `"${tenant.house_number || ''}"`,
        `"${tenant.house_id || ''}"`,
        `"${tenant.apartment || ''}"`,
        `"${tenant.apartment_id || ''}"`,
        `"${tenant.month || ''}"`,
        `"${tenant.year || ''}"`,
        `"${tenant.expected_amount || 0}"`,
        `"${tenant.opening_balance_amount || 0}"`,
        `"${tenant.rent_amount || 0}"`,
        `"${tenant.status || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `payment_history_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SkeletonRow = () => (
    <tr>
      {[...Array(7)].map((_, idx) => (
        <td key={idx} className="px-4 py-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={12} className="rounded w-full h-4" />
          </Placeholder>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="bg-white shadow mt-5 border-0 rounded-lg">
      <h5 className="ms-4 mt-4 mb-0 py-3 font-semibold text-gray-800 text-xl">Payment Histories</h5>

      <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-4 px-6 py-2 border-gray-100">
        {/* Search input */}
        <InputGroup className="w-25 md:w-auto">
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          />
        </InputGroup>
        <div className="flex md:flex-row flex-col md:items-center gap-2">
          {/* Apartment filter select */}
          <select
            className="focus:ring-opacity-50 px-3 py-2 border border-gray-300 focus:border-blue-400 rounded-md focus:ring focus:ring-blue-200"
            value={apartmentFilter}
            onChange={e => {
              setApartmentFilter(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          >
            <option value="">All Properties</option>
            {apartmentOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          {/* Export button */}
          <Button
            variant="outline-success"
            onClick={exportToCSV}
            disabled={isLoading || filteredTenants.length === 0}
          >
            {/* <FaFileExport className="mr-2" /> */}
            Export CSV
          </Button>
        </div>
      </div>
      <div className="px-0 py-4">
        <div className="border border-gray-100 rounded-lg overflow-x-auto">
          <table className="divide-y divide-gray-200 min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">House Number</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Tenant Name</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Phone</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Month/Year</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Opening Balance</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Rent Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Expected Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Opening Balance Due Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Rent Balance Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Opening Balance Paid Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Rent Paid Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Payment Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading ? (
                Array.from({ length: 9 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : currentItems?.length > 0 ? (
                currentItems.map(tenant => (
                  <PaymentRow
                    key={tenant.id}
                    tenant={tenant}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="py-6 text-gray-400 text-center">
                    {searchTerm ? 'No payment history found matching your search' : 'No Payments Found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && filteredTenants?.length > itemsPerPage && (
          <div className="flex md:flex-row flex-col justify-between items-center gap-2 mt-6">
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="text-gray-600 text-sm">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTenants?.length)} of {filteredTenants?.length}
            </span>
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;